import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { products } from '../data/products';

export const ProductDetail = () => {
  const { productSlug } = useParams();
  
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productSlug]);
  
  // Find the product based on slug
  const product = products.find(p => p.slug === productSlug);

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-4xl font-light text-stone-900">Product not found</h1>
          <Link to="/" className="text-stone-600 hover:text-stone-900 mt-4 inline-block">
            Return to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-28 pb-4">
        <Link 
          to={`/collection/${product.collection}`}
          onClick={() => {
            setTimeout(() => {
              window.scrollTo(0, 0);
            }, 100);
          }}
        >
          <Button variant="ghost" className="text-stone-600 hover:text-stone-900">
            <ArrowLeft className="mr-2" size={20} />
            Back to Collection
          </Button>
        </Link>
      </div>

      {/* Product Image */}
      <section className="pb-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-stone-100 overflow-hidden">
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-auto object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-stone-900 text-center mt-6">
            {product.name}
          </h1>
        </div>
      </section>

      {/* Product Description */}
      <section className="py-12 bg-stone-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-stone-900 mb-6">Product Description</h2>
          <div className="prose prose-stone max-w-none">
            {product.description.split('\n').map((paragraph, index) => (
              <p key={index} className="text-stone-600 leading-relaxed mb-4">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* General Specification */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-stone-900 mb-6">General Specifications</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {product.generalSpec.map((spec, index) => (
              <div key={index} className="flex border-b border-stone-200 py-3">
                <span className="font-medium text-stone-900 w-1/2">{spec.label}</span>
                <span className="text-stone-600 w-1/2">{spec.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Details */}
      <section className="py-12 bg-stone-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-stone-900 mb-6">Technical Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {product.technicalDetails.map((detail, index) => (
              <div key={index} className="flex border-b border-stone-200 py-3">
                <span className="font-medium text-stone-900 w-1/2">{detail.label}</span>
                <span className="text-stone-600 w-1/2">{detail.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-stone-900 mb-8 text-center">Related Products</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products
              .filter(p => p.collection === product.collection && p.slug !== product.slug)
              .sort((a, b) => {
                // Create a unique rotation based on current product ID
                const offsetA = (a.id + product.id) % 11;
                const offsetB = (b.id + product.id) % 11;
                return offsetA - offsetB;
              })
              .slice(0, 4)
              .map((relatedProduct) => (
                <Link 
                  key={relatedProduct.id} 
                  to={`/product/${relatedProduct.slug}`}
                  className="group"
                >
                  <div className="aspect-square bg-stone-100 overflow-hidden mb-4">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <p className="text-center font-bold text-stone-900 group-hover:text-stone-600 transition-colors">
                    {relatedProduct.name}
                  </p>
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-light tracking-tight text-stone-900 mb-6">
            Interested in this fan?
          </h2>
          <p className="text-lg text-stone-600 leading-relaxed mb-8">
            Visit our showrooms to see it in person or contact us for more information.
          </p>
          <Link 
            to="/display-centres"
            onClick={() => window.scrollTo(0, 0)}
          >
            <Button className="bg-stone-900 hover:bg-stone-800 text-stone-50 px-10 py-4 text-sm tracking-widest transition-all duration-300">
              VISIT SHOWROOMS
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};
